import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(null); // 'valid', 'invalid', 'expired', 'used'
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenStatus('invalid');
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
        const data = await response.json();

        if (data.valid) {
          setTokenStatus('valid');
        } else {
          setTokenStatus(data.reason || 'invalid');
        }
      } catch (error) {
        setTokenStatus('invalid');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        toast.success('Contraseña actualizada correctamente');
      } else {
        toast.error(data.message || data.detail || 'Error al actualizar la contraseña');
      }
    } catch (error) {
      toast.error('Error de conexión. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#1F3A5F] mx-auto mb-4" />
            <p className="text-slate-600">Verificando enlace...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid/Expired token state
  if (tokenStatus !== 'valid' && !success) {
    let title = 'Enlace no válido';
    let description = 'El enlace de recuperación no es válido.';

    if (tokenStatus === 'expired') {
      title = 'Enlace expirado';
      description = 'El enlace de recuperación ha expirado. Los enlaces son válidos por 15 minutos.';
    } else if (tokenStatus === 'used') {
      title = 'Enlace ya utilizado';
      description = 'Este enlace de recuperación ya fue utilizado.';
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {title}
            </CardTitle>
            <CardDescription className="text-base">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link to="/forgot-password" className="block">
              <Button className="w-full bg-[#1F3A5F] hover:bg-[#2d4a6f]">
                Solicitar nuevo enlace
              </Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full text-slate-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al inicio de sesión
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#1F3A5F]">
              ¡Contraseña actualizada!
            </CardTitle>
            <CardDescription className="text-base">
              Tu contraseña ha sido cambiada correctamente. Ya podés iniciar sesión con tu nueva contraseña.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button className="w-full bg-[#1F3A5F] hover:bg-[#2d4a6f]">
                Iniciar sesión
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#1F3A5F]/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-[#1F3A5F]" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1F3A5F]">
            Crear nueva contraseña
          </CardTitle>
          <CardDescription>
            Ingresá tu nueva contraseña. Debe tener al menos 6 caracteres.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nueva contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  data-testid="reset-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                data-testid="reset-confirm-password"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#1F3A5F] hover:bg-[#2d4a6f]"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Actualizando...
                </>
              ) : (
                'Actualizar contraseña'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
